import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { PermissionType } from '~/data/sdk';
import { api } from '~/data/utils/axios-instance/api-axios-instance';
import { usePermissionsStore } from '~/stores/permissions.store';
import { AUTH_BYPASS_ENABLED } from '~/lib/dev-auth-bypass.constants';
import { useAuthToken } from './use-auth-token';

export const usePermissions = () => {
  const { isAuthenticated, isLoading: auth0Loading } = useAuth0();
  const token = useAuthToken();
  const hasFetchedRef = useRef(false);
  const [isApiLoading, setIsApiLoading] = useState(false);

  // Granular selectors so this hook re-runs only when the *fields it actually
  // reads* change. The previous `usePermissionsStore()` destructure subscribed
  // to the entire store object, causing every consumer of `usePermissions()`
  // to re-render on any permission-store update.
  const auth0Permissions = usePermissionsStore((state) => state.auth0Permissions);
  const userPermissions = usePermissionsStore((state) => state.userPermissions);
  const allPermissions = usePermissionsStore((state) => state.allPermissions);
  // isCFO/hasCopilotAccess/roleFetched live in the shared store (not local useState) so a
  // freshly-mounted component reads the already-fetched value instead of a stale per-mount
  // default of `false` while another component's earlier fetch is still the one backing the cache.
  const isCFO = usePermissionsStore((state) => state.isCFO);
  const hasCopilotAccess = usePermissionsStore((state) => state.hasCopilotAccess);
  const roleFetched = usePermissionsStore((state) => state.roleFetched);

  // Action references in Zustand are stable across renders, so selecting them
  // individually is referentially safe and avoids the wide subscription above.
  const setAuth0Permissions = usePermissionsStore((state) => state.setAuth0Permissions);
  const setUserPermissions = usePermissionsStore((state) => state.setUserPermissions);
  const setCurrentRole = usePermissionsStore((state) => state.setCurrentRole);
  const markRoleFetchAttempted = usePermissionsStore((state) => state.markRoleFetchAttempted);
  const clearPermissions = usePermissionsStore((state) => state.clearPermissions);
  const hasPermission = usePermissionsStore((state) => state.hasPermission);
  const hasActivityPermission = usePermissionsStore((state) => state.hasActivityPermission);
  const isAdmin = usePermissionsStore((state) => state.isAdmin);
  const isOwner = usePermissionsStore((state) => state.isOwner);
  const isCacheValid = usePermissionsStore((state) => state.isCacheValid);

  // Subscribe reactively to `lastFetched` so this hook re-runs its fetch effect
  // whenever the store is externally invalidated (e.g. `invalidateRbacCaches`
  // after an admin mutation sets `lastFetched` back to `null`). Without this
  // reactive tie, `clearPermissions()` would empty the store but the
  // `hasFetchedRef` gate below would still block a refetch, leaving the user
  // with `allPermissions: []` and `isAdmin() === false` until a token refresh
  // or logout.
  const lastFetched = usePermissionsStore((state) => state.lastFetched);

  // Reset the "already fetched" gate when the store signals invalidation
  // (`lastFetched === null`). Runs before the fetch effect below because both
  // react to the same `lastFetched` change in the same render tick.
  useEffect(() => {
    if (lastFetched === null) {
      hasFetchedRef.current = false;
    }
  }, [lastFetched]);

  // Sync Auth0 permissions when token changes
  useEffect(() => {
    if (token?.permissions) {
      setAuth0Permissions(token.permissions);
    } else {
      setAuth0Permissions([]);
    }
    // Reset fetch flag when token changes (user logs in/out)
    hasFetchedRef.current = false;
  }, [token?.permissions, setAuth0Permissions]);

  useEffect(() => {
    if (!isAuthenticated || !token || hasFetchedRef.current) {
      return;
    }

    const fetchCurrentUserRoleAndPermissions = async () => {
      if (isCacheValid()) {
        hasFetchedRef.current = true;
        return;
      }

      setIsApiLoading(true);

      try {
        const { data: currentRole } = await api.role.getCurrentRole();

        setCurrentRole({ isCFO: currentRole.isCfo, hasCopilotAccess: currentRole.hasCopilotAccess });

        const permissions = currentRole.permissions.map((permission) => ({
          id: permission.id,
          activityId: permission.activityId || '',
          type: permission.type,
        }));

        setUserPermissions(permissions);
        hasFetchedRef.current = true;
      } catch (error) {
        console.warn('Failed to fetch current user role, falling back to Auth0 permissions:', error);
        // Settle `roleFetched` without touching isCFO/hasCopilotAccess, so a transient failure
        // doesn't wipe out a previously-known role and downstream consumers stop blocking on it.
        markRoleFetchAttempted();
      } finally {
        setIsApiLoading(false);
      }
    };

    fetchCurrentUserRoleAndPermissions();
    // `lastFetched` is in the dep array so that when `invalidateRbacCaches` clears
    // the store (setting it to `null`), this effect re-runs and refetches.
  }, [isAuthenticated, token, lastFetched, isCacheValid, setUserPermissions, setCurrentRole, markRoleFetchAttempted]);

  // Clear permissions when user logs out
  useEffect(() => {
    if (!isAuthenticated || !token) {
      clearPermissions();
      hasFetchedRef.current = false;
    }
  }, [isAuthenticated, token, clearPermissions]);

  // Return early with safe defaults if Auth0 is still loading or not authenticated
  if (!AUTH_BYPASS_ENABLED && (auth0Loading || !isAuthenticated)) {
    return {
      auth0Permissions: [],
      userPermissions: [],
      allPermissions: [],
      isLoading: true,
      hasPermission: () => false,
      hasActivityPermission: () => false,
      isAdmin: () => false,
      isOwner: () => false,
      isCFO: false,
      hasCopilotAccess: false,
      hasAnyPermission: () => false,
      hasAllPermissions: () => false,
      canViewActivity: () => false,
      canEditActivity: () => false,
      canManageActivity: () => false,
    };
  }

  return {
    auth0Permissions,
    userPermissions,
    allPermissions,
    // `!roleFetched` covers the gap between mount and the first settled GET /roles/current
    // call (whether in flight here or already resolved by another component) — without it,
    // isLoading can read `false` before isCFO/hasCopilotAccess have ever been determined.
    isLoading: auth0Loading || isApiLoading || !roleFetched,

    hasPermission,
    hasActivityPermission,
    isAdmin,
    isOwner,
    isCFO,
    hasCopilotAccess,

    hasAnyPermission: (permissions: string[]) => permissions.some((p) => hasPermission(p)),
    hasAllPermissions: (permissions: string[]) => permissions.every((p) => hasPermission(p)),

    canViewActivity: (activityId: string) =>
      isAdmin() ||
      hasActivityPermission(activityId, PermissionType.Owner) ||
      hasActivityPermission(activityId, PermissionType.Manager) ||
      hasActivityPermission(activityId, PermissionType.Analyst),

    canEditActivity: (activityId: string) =>
      isAdmin() ||
      hasActivityPermission(activityId, PermissionType.Owner) ||
      hasActivityPermission(activityId, PermissionType.Manager),

    canManageActivity: (activityId: string) => isAdmin() || hasActivityPermission(activityId, PermissionType.Owner),
  };
};
